import { useMemo } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { PO_LIST_PER_PAGE } from "../../Components/NewPurchaseOrder/constants";
import {
  buildPurchaseOrdersListPayload,
  fetchPurchaseOrdersList,
} from "../../services/purchaseOrders/purchaseOrdersApi";
import { normalizeListFilters, purchaseOrderKeys } from "./queryKeys";

/** Same store and page size — only search / status / vendor / sort changed. */
const isSameListScope = (previousFilters, filters) =>
  Boolean(previousFilters) &&
  previousFilters.merchantId === filters.merchantId &&
  previousFilters.perPage === filters.perPage;

/**
 * Infinite scroll listing — `page` / `per_page` on each request.
 * Query key is filter-scoped (page is not part of the key).
 */
export function usePurchaseOrdersInfiniteListQuery({
  auth,
  merchantId,
  search,
  status,
  sortCol,
  sortOrd,
  vendorId,
  perPage = PO_LIST_PER_PAGE,
  enabled = true,
}) {
  const filters = useMemo(
    () =>
      normalizeListFilters({
        merchantId,
        search,
        status,
        sortCol,
        sortOrd,
        vendorId,
        perPage,
      }),
    [merchantId, search, status, sortCol, sortOrd, vendorId, perPage]
  );

  return useInfiniteQuery({
    queryKey: purchaseOrderKeys.list(filters),
    queryFn: ({ pageParam, signal }) =>
      fetchPurchaseOrdersList(
        buildPurchaseOrdersListPayload(auth, filters, pageParam),
        signal
      ),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.meta.hasMore ? lastPage.meta.page + 1 : undefined,
    enabled: Boolean(enabled && merchantId && auth?.token),
    refetchOnMount: "always",
    // Changing search / status / vendor / sort keeps the current rows on screen
    // until the new list arrives, so the page does not collapse to a skeleton
    // and jump. A different store starts empty so its POs never mix.
    placeholderData: (previousData, previousQuery) => {
      const previousKey = previousQuery?.queryKey;
      const previousFilters = previousKey?.[previousKey.length - 1];
      return isSameListScope(previousFilters, filters) ? previousData : undefined;
    },
  });
}
